import { Clock, CheckCircle, ArrowRight, Calendar, ChevronRight, BookOpen, HelpCircle, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;
const HOMEPAGE_URL = process.env.REACT_APP_HOMEPAGE_URL;
const PHOTO_URL = 'https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/ytwcldm5_BFB_0045.jpg';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/24wkpkvm_logo.png';

const testimonials = [
  { id: 1, name: "Domenick", text: "My wife and I were looking to make a change from Medicare to Medicare Advantage to save money. Adeen worked with us until he found the right plan for us which is working great. We have already recommended him to family and friends and he is always available to answer any questions. If I could give him 10 Stars I would!", rating: 5, location: "NY Tri-State Area" },
  { id: 2, name: "Steven", text: "Adeen was the best agent I ever had. My wife has end stage renal disease which requires dialysis treatment. Adeen was very helpful and helped us find the right plan. He went to the extent to check all the doctors and I feel he went well above and beyond his duty. I would highly recommend Adeen for your Medicare and other insurance needs. He's very knowledgeable, helpful and friendly.", rating: 5, location: "NY Tri-State Area" },
  { id: 3, name: "Sally", text: "Adeen was very helpful in assisting me with decisions regarding my advantage plan. He listened to my concerns and took the time to research what would work best for me. I appreciated so much having his expertise in helping me to make these decisions.", rating: 5, location: "Westchester County, NY" },
  { id: 4, name: "Leanne", text: "Adeen was very helpful with my changing from NY State Health to Medicare Advantage. He took the time to research what companies covered all of my doctors.", rating: 5, location: "New York" },
  { id: 5, name: "Brandon", text: "Adeen was very knowledgeable about the different plans available and helped me pick a plan that met my needs. He walked me through the whole process and made sure my enrollment went through without a hitch.", rating: 5, location: "Westchester County, NY" },
  { id: 6, name: "Shana", text: "Adeen was professional, thorough, and extremely helpful in helping choose the right healthcare providers for my mother. He made it easy and was a pleasure to work with!", rating: 5, location: "NY Tri-State Area" },
];

const handleBookClick = () => {
  if (window.gtag_report_conversion) {
    window.gtag_report_conversion(CALENDLY_URL);
  }
};

export default function MedicareLanding() {
  return (
    <div className="min-h-screen bg-white">
      {/* Slim Top Bar */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
          <a href={HOMEPAGE_URL} className="flex items-center space-x-2 sm:space-x-3 min-w-0" data-testid="logo-link">
            <img src={LOGO_URL} alt="TruBridge Health" className="h-9 sm:h-12 w-auto flex-shrink-0" />
            <div className="min-w-0">
              <span className="font-bold text-base sm:text-xl text-gray-900 block leading-tight">TruBridge Health</span>
              <span className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">Your Trusted Partner in Medicare & Health</span>
            </div>
          </a>
          <a href={CALENDLY_URL} onClick={handleBookClick} className="flex-shrink-0 ml-2 hidden sm:block">
            <Button className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-3 sm:px-4 py-2" data-testid="header-book-btn">
              <Calendar className="h-4 w-4 mr-1 sm:mr-2" />
              Book Free Session
            </Button>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="container mx-auto px-4 py-10 md:py-20 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16 max-w-6xl mx-auto">
            {/* Left - Content */}
            <div className="flex-1 text-center md:text-left">
              <div className="inline-block mb-4 md:mb-5 px-3 md:px-4 py-1.5 md:py-2 bg-yellow-400/90 text-gray-900 rounded-full text-xs sm:text-sm font-bold tracking-wide">
                FREE 15-MINUTE MEDICARE Q&A SESSION
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-4 md:mb-5 leading-tight" data-testid="hero-headline">
                Turning 65?<br />
                <span className="text-blue-300">Get Your Medicare Questions Answered.</span>
              </h1>
              <p className="text-base sm:text-lg text-blue-100/90 mb-6 md:mb-8 leading-relaxed max-w-xl mx-auto md:mx-0">
                Medicare can be overwhelming — Parts A, B, C, D, Advantage, Supplement, enrollment deadlines. In a free 15-minute session, I'll answer your Medicare questions in plain English so you can feel confident about what comes next.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center md:justify-start">
                <a href={CALENDLY_URL} onClick={handleBookClick} data-testid="hero-book-btn">
                  <Button size="lg" className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full sm:w-auto transition-all hover:scale-105 shadow-lg shadow-yellow-400/20">
                    <Calendar className="mr-2 h-5 w-5" />
                    Book Your Free Q&A Session
                  </Button>
                </a>
              </div>

              <div className="flex items-center gap-4 sm:gap-6 mt-6 md:mt-8 justify-center md:justify-start text-blue-200/70 text-xs sm:text-sm">
                <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> 15 min</span>
                <span className="flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Educational only</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> 100% free</span>
              </div>
            </div>

            {/* Right - Photo */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-br from-blue-400 to-yellow-400 rounded-2xl opacity-30 blur-sm"></div>
                <img
                  src={PHOTO_URL}
                  alt="Adeen Schwartz - Licensed Medicare Agent"
                  className="relative w-48 h-48 sm:w-72 sm:h-72 md:w-96 md:h-96 object-cover rounded-2xl shadow-2xl"
                  data-testid="adeen-photo"
                />
                <div className="absolute -bottom-3 sm:-bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-full px-4 sm:px-5 py-1.5 sm:py-2 shadow-lg">
                  <p className="text-xs sm:text-sm font-bold text-gray-900 whitespace-nowrap">Adeen Schwartz</p>
                  <p className="text-[10px] sm:text-xs text-gray-500 text-center">Licensed Medicare Agent</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points / Questions Section */}
      <section className="py-10 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center text-gray-900 mb-3">
            Medicare Is Confusing. It Doesn't Have to Be.
          </h2>
          <p className="text-center text-gray-500 mb-8 md:mb-10 text-sm sm:text-base">
            If you're approaching 65, you probably have a lot of questions. You're not alone — here are some of the most common ones I hear:
          </p>

          <div className="grid sm:grid-cols-2 gap-3 sm:gap-5">
            {[
              { id: 'q-advantage', q: '"What\'s the difference between Original Medicare and Medicare Advantage?"' },
              { id: 'q-enroll', q: '"When do I need to enroll? What happens if I miss my window?"' },
              { id: 'q-parts', q: '"What do Parts A, B, C, and D actually cover?"' },
              { id: 'q-medigap', q: '"What\'s a Medigap plan and do I need one?"' },
              { id: 'q-employer', q: '"How does Medicare work with my employer insurance?"' },
              { id: 'q-cost', q: '"What will Medicare actually cost me each month?"' },
            ].map((item) => (
              <div key={item.id} className="bg-white rounded-xl p-4 sm:p-5 border border-gray-200 shadow-sm flex items-start gap-3" data-testid={`pain-point-${item.id}`}>
                <HelpCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700 italic text-sm sm:text-base leading-relaxed">{item.q}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-10">
            <p className="text-base sm:text-lg text-gray-700 font-medium mb-5">
              I answer these questions every day. Let me help you understand Medicare.
            </p>
            <a href={CALENDLY_URL} onClick={handleBookClick} data-testid="pain-section-book-btn">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-4 sm:py-5 text-base sm:text-lg transition-all hover:scale-105">
                <Calendar className="mr-2 h-5 w-5" />
                Get Your Questions Answered — Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Trust / About Adeen */}
      <section className="py-10 md:py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <img
              src={PHOTO_URL}
              alt="Adeen Schwartz"
              className="w-32 h-32 sm:w-48 sm:h-48 rounded-xl object-cover shadow-xl flex-shrink-0"
            />
            <div className="text-center md:text-left">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">Healthcare Expertise You Can Trust. Medicare Guidance You Can Understand.</h2>
              <p className="text-blue-300 font-medium mb-3 sm:mb-4 text-sm sm:text-base">Licensed Health & Life Agent | NPN# 21606228</p>
              <p className="text-blue-100/80 leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                With over 15 years of experience serving individuals and families throughout Westchester County and the NY Tri-State area, I founded TruBridge Health because I believe everyone deserves to understand their Medicare options — without the confusion or pressure.
              </p>
              <p className="text-blue-100/80 leading-relaxed text-sm sm:text-base">
                My goal is to educate. I break down the complexities of Medicare into simple, clear terms so you can feel informed and confident. When you're ready to explore specific plans, that's a separate conversation entirely.
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3 mt-4 sm:mt-5 justify-center md:justify-start">
                {['AHIP Certified', 'Licensed Insurance Agent', 'Medicare Specialist', '15+ Years Experience'].map((tag) => (
                  <span key={tag} className="px-3 py-1.5 bg-white/10 text-blue-200 rounded-full text-xs font-medium border border-white/10">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-10 md:py-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3">5-Star Rated on Google</h2>
            <p className="text-gray-500 text-sm sm:text-base">See why our clients trust us with their Medicare questions.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-all border-l-4 border-l-blue-600">
                <CardHeader>
                  <div className="flex items-center space-x-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${testimonial.id}-${i}`} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                  <CardDescription className="text-sm">{testimonial.location}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 italic leading-relaxed">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-10 md:py-16 bg-blue-600" data-testid="final-cta">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4">
            Have Medicare Questions? Let's Talk.
          </h2>
          <p className="text-blue-100 text-base sm:text-lg mb-6 sm:mb-8">
            Schedule a free 15-minute educational session. I'll answer your questions about Medicare — how it works, what it covers, and what you need to know. No plan recommendations, no pressure.
          </p>
          <a href={CALENDLY_URL} onClick={handleBookClick} data-testid="final-book-btn">
            <Button size="lg" className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg transition-all hover:scale-105 shadow-lg">
              <Calendar className="mr-2 h-5 w-5" />
              Book My Free Q&A Session
            </Button>
          </a>
          <p className="text-blue-200/60 text-sm mt-4">Free | 15 minutes | Educational only | No obligation</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
            <div className="flex items-center space-x-2">
              <img src={LOGO_URL} alt="TruBridge Health" className="h-8 w-auto" />
              <span className="text-white font-semibold">TruBridge Health</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <a href={HOMEPAGE_URL} className="hover:text-white transition-colors flex items-center gap-1" data-testid="visit-homepage-link">
                Visit Full Website <ChevronRight className="h-3 w-3" />
              </a>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-4">
            <div className="flex flex-col md:flex-row justify-between items-start gap-4">
              <div className="text-xs leading-relaxed max-w-2xl">
                <p className="mb-2">
                  <strong className="text-gray-300">Important:</strong> This session is educational only. We do not offer every plan available in your area. Any information we provide is limited to those plans we do offer in your area. Please contact Medicare.gov or 1-800-MEDICARE to get information on all of your options.
                </p>
                <p>
                  TruBridge Health is not connected with or endorsed by the U.S. government or the federal Medicare program.
                </p>
              </div>
              <div className="text-xs text-right flex-shrink-0">
                <p className="text-white font-medium">Adeen Schwartz</p>
                <p>Licensed Health and Life Agent</p>
                <p>NPN# 21606228</p>
              </div>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500 mt-6">&copy; {new Date().getFullYear()} TruBridge Health. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
